import React from 'react';

import {
  View,
  Image,
  StyleSheet,
  Dimensions,
  Animated,
  PanResponder,
  InteractionManager,
} from 'react-native';

import OPComponent from './op_component';
import { COLOR } from '../../constants/styles';

const MAX_SCALE = 3.0;
const CLICK_DISTANCE = 5;
const DBLCLICK_INTERVAL = 200;
const ANIMATION_DURATION = 100;
const BOUNCE_DISTANCE = 50;
const SWIPE_DISTANCE = 20;

export default class ZoomableImageView extends OPComponent {
  constructor(props) {
    super(props);

    this.state = {
      animatedWidth: new Animated.Value(0),
      animatedHeight: new Animated.Value(0),
      animatedX: new Animated.Value(0),
      animatedY: new Animated.Value(0),
      gotSize: false,
    };

    this._imageWidth;
    this._imageHeight;

    this._width = 0;
    this._height = 0;
    this._origWidth = 0;
    this._origHeight = 0;
    this._maxWidth = 0;
    this._maxHeight = 0;

    this._tanslateX = 0;
    this._tanslateY = 0;

    this._maxDX = 0;
    this._maxDY = 0;

    this._bouncedYOnce = false;
    this._bouncedLeftOnce = false;
    this._bouncedRightOnce = false;
    this._atOrigSize = true;
    this._isClosing = false;
    this._isSwiping = false;

    this._screenHeight;
    this._screenWidth;
  }

  componentDidMount() {
    this.panResponder = this.createPanResponder();
  }

  getImageSize() {
    if (this._imageWidth != null) {
      return Promise.resolve({
        imgWidth: this._imageWidth,
        imgHeight: this._imageHeight,
      });
    } else {
      return new Promise((resolve, reject) => {
        Image.getSize(
          this.props.imageUri,
          (imgWidth, imgHeight) => {
            this._imageWidth = imgWidth;
            this._imageHeight = imgHeight;

            resolve({ imgWidth, imgHeight });

            // set state here to indicate its ok now to render the Image tag
            this.setState({ gotSize: true });
          },
          (e) => {
            reject(e);
          }
        );
      });
    }
  }

  handleLayout = () => {
    const { width, height } = Dimensions.get('window');

    // only do this on first layout, and upon screen rotation
    if (this._screenHeight == height && this._screenWidth == width) return;

    this._screenHeight = height;
    this._screenWidth = width;

    this.getImageSize()
      .then(({ imgWidth, imgHeight }) => {
        let renderWidth;
        let renderHeight;

        if (imgWidth <= width && imgHeight <= height) {
          renderWidth = imgWidth;
          renderHeight = imgHeight;
        } else {
          const wRatio = (width * 1.0) / imgWidth;
          const hRatio = (height * 1.0) / imgHeight;
          const biggestRatio = Math.min(wRatio, hRatio);
          renderWidth = imgWidth * biggestRatio;
          renderHeight = imgHeight * biggestRatio;
        }

        this._width = this._origWidth = parseInt(renderWidth);
        this._height = this._origHeight = parseInt(renderHeight);

        this._maxWidth = this._width * MAX_SCALE;
        this._maxHeight = this._height * MAX_SCALE;

        this.setImageWidthHeight(this._width, this._height);
        this.setXY(0, 0);
      })
      .catch((e) => {
        this.props.onError && this.props.onError();
      });
  };

  swipe(dir) {
    this.props.onSwipeHorizontal && this.props.onSwipeHorizontal(dir);
    this._bouncedYOnce = false;

    this._bouncedLeftOnce = false;
    this._bouncedRightOnce = false;

    this._isSwiping = true;
  }

  resetZoomSize() {
    this.setImageWidthHeight(this._origWidth, this._origHeight);
    this.setXY(0, 0);
  }

  atOrigSize() {
    this._atOrigSize = this._width == this._origWidth && this._height == this._origHeight;
  }

  scaleImage(scale, withAnimation = false) {
    let newWidth = parseInt(this._width * scale);
    let newHeight = parseInt(this._height * scale);

    // don't get too big
    if (newWidth >= this._maxWidth) {
      newWidth = this._maxWidth;
      newHeight = this._maxHeight;
    }
    // dont get smaller than original which fit in screen
    if (newWidth <= this._origWidth) {
      newWidth = this._origWidth;
      newHeight = this._origHeight;
    }

    this.setImageWidthHeight(newWidth, newHeight, withAnimation);
  }

  setImageWidthHeight(
    width,
    height,
    withAnimation = false,
    onAnimationComplete = null,
    toCenter = false
  ) {
    this._width = parseInt(width);
    this._height = parseInt(height);

    // console.info('setImageWidthHeight', width, height);

    if (withAnimation) {
      this.animateWidthHeight(this._width, this._height, onAnimationComplete, toCenter);
    } else {
      this.state.animatedWidth.setValue(this._width);
      this.state.animatedHeight.setValue(this._height);
    }

    this._bouncedYOnce = false;

    this._bouncedLeftOnce = false;
    this._bouncedRightOnce = false;

    this.atOrigSize();
  }

  calculateMaxDXDY() {
    const widthDiff = this._width - this._screenWidth;
    const heightDiff = this._height - this._screenHeight;
    const absMaxDX = widthDiff <= 0 ? 0 : widthDiff / 2.0;
    const absMaxDY = heightDiff <= 0 ? 0 : heightDiff / 2.0;

    this._maxDX = absMaxDX;
    this._maxDY = absMaxDY;
  }

  moveXY(x, y) {
    if (this._isSwiping) return;

    const maxDX = this._maxDX + BOUNCE_DISTANCE;
    const maxDY = this._maxDY + BOUNCE_DISTANCE;

    const swipeDX = this._maxDX + SWIPE_DISTANCE;
    const swipeDY = this._maxDY + SWIPE_DISTANCE;

    if (Math.abs(x) > this._maxDX) {
      if (x > swipeDX) {
        if (this._bouncedLeftOnce || this._atOrigSize) {
          this.swipe(1);
          return;
        }
      }
      if (x * -1 > swipeDX) {
        if (this._bouncedRightOnce || this._atOrigSize) {
          this.swipe(-1);
          return;
        }
      }

      if (Math.abs(x) > maxDX) {
        const dir = x < 0 ? -1 : 1;
        x = maxDX * dir;
        this._bouncedYOnce = false;
      }
    }

    if (Math.abs(y) > maxDY) {
      const dir = y < 0 ? -1 : 1;
      // dragging down
      if (dir > 0) {
        const doBounce = !this._atOrigSize && !this._bouncedYOnce;
        if (doBounce || !this.props.onClose) {
          y = maxDY * dir;
        } else {
          this._isClosing = true;
          this.props.onClose();
        }
      } else {
        // restrict how far they can pull
        y = maxDY * dir;
        this._bouncedYOnce = false;
        this._bouncedRightOnce = false;
        this._bouncedLeftOnce = false;
      }
    }

    this.setXY(x, y, false);
    this._afterPan = true;
  }

  setXYOnRelease(x, y, afterPan) {
    const maxDX = this._maxDX;
    const maxDY = this._maxDY;

    if (Math.abs(x) > maxDX) {
      const dir = x < 0 ? -1 : 1;
      if (afterPan) {
        if (dir < 0) {
          this._bouncedRightOnce = true;
        } else {
          this._bouncedLeftOnce = true;
        }
      }
      x = maxDX * dir;
    }
    if (Math.abs(y) > maxDY) {
      const dir = y < 0 ? -1 : 1;
      // dragging down
      this._bouncedYOnce = dir > 0 && afterPan;
      y = maxDY * dir;
    }

    this.setXY(x, y, true);
  }

  setXY(x, y, withAnimation = false) {
    this._tanslateX = x;
    this._tanslateY = y;

    if (withAnimation) {
      this.animatePos(x, y);
    } else {
      this.state.animatedX.setValue(x);
      this.state.animatedY.setValue(y);
    }
  }

  handleRelease = () => {
    if (this._isClosing) return;

    this._isSwiping = false;

    // first re-calculate max dx,dy in case we've scaled up/down
    this.calculateMaxDXDY();

    this.setXYOnRelease(this._tanslateX, this._tanslateY, this._afterPan);
    this._afterPan = false;
  };

  handleDblClick(onAnimationComplete) {
    // if we're scaled all the way up scale back to original size, otherwise
    // scale up to max
    if (this._width == this._maxWidth) {
      this.setImageWidthHeight(this._origWidth, this._origHeight, true, onAnimationComplete, true);
    } else {
      this.setImageWidthHeight(this._maxWidth, this._maxHeight, true, onAnimationComplete);
    }
  }

  animateWidthHeight(width, height, onAnimationComplete, toCenter) {
    let animationArray = [
      Animated.timing(this.state.animatedWidth, {
        toValue: width,
        duration: ANIMATION_DURATION,
        useNativeDriver: false,
      }),
      Animated.timing(this.state.animatedHeight, {
        toValue: height,
        duration: ANIMATION_DURATION,
        useNativeDriver: false,
      }),
    ];

    if (toCenter) {
      animationArray = animationArray.concat([
        Animated.timing(this.state.animatedX, {
          toValue: 0,
          duration: ANIMATION_DURATION,
          useNativeDriver: false,
        }),
        Animated.timing(this.state.animatedY, {
          toValue: 0,
          duration: ANIMATION_DURATION,
          useNativeDriver: false,
        }),
      ]);
    }

    Animated.parallel(animationArray).start(onAnimationComplete);
  }

  animatePos(x, y) {
    Animated.parallel([
      Animated.timing(this.state.animatedX, {
        toValue: x,
        duration: ANIMATION_DURATION,
        useNativeDriver: false,
      }),
      Animated.timing(this.state.animatedY, {
        toValue: y,
        duration: ANIMATION_DURATION,
        useNativeDriver: false,
      }),
    ]).start();
  }

  createPanResponder() {
    let _startX = null;
    let _startY = null;
    let _lastScaleDistance = null;
    let _clickTimeout = null;
    let _moveDir = null;

    return PanResponder.create({
      onStartShouldSetPanResponder: (evt, gestureState) => true,
      onPanResponderGrant: (evt, gestureState) => {
        _startX = this._tanslateX;
        _startY = this._tanslateY;
        _lastScaleDistance = null;
        _moveDir = null;
      },
      onPanResponderMove: (evt, gestureState) => {
        if (evt.nativeEvent.changedTouches.length <= 1) {
          const { dx, dy } = gestureState;

          // console.info('onPanResponderMove', dx, dy);

          let moveX = _startX;
          let moveY = _startY;

          if (this._atOrigSize) {
            // if we're at the original size restrict movement to only one access
            // to make swipe left/right or swiping down to close more smooth
            if (_moveDir == null) _moveDir = Math.abs(dx) - Math.abs(dy);
            if (_moveDir < 0) {
              // dy is greater
              moveY += dy;
            } else {
              // dx is greater
              moveX += dx;
            }
          } else {
            // if we're zoomed in then enable bi-directional panning
            moveY += dy;
            moveX += dx;
          }

          this.moveXY(moveX, moveY);
        } else {
          const touches1 = evt.nativeEvent.changedTouches[0];
          const touches2 = evt.nativeEvent.changedTouches[1];
          const dx = Math.abs(touches1.pageX - touches2.pageX);
          const dy = Math.abs(touches1.pageY - touches2.pageY);
          const distance = parseFloat(Math.sqrt(dx * dx + dy * dy));

          // we dont know yet if we're zooming in or out
          if (_lastScaleDistance == null) {
            _lastScaleDistance = distance;
            return;
          }

          // determine if we're zooming in or out, scale accordingly
          const dDiff = distance - _lastScaleDistance;
          const scale = dDiff == 0 ? 1 : dDiff > 0 ? 1.05 : 0.95;

          this.scaleImage(scale);

          _lastScaleDistance = distance;
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        if (evt.nativeEvent.changedTouches.length <= 1) {
          const { dx, dy } = gestureState;
          const moveDistance = Math.sqrt(dx * dx + dy * dy);

          // handle as tap ?
          if (moveDistance < CLICK_DISTANCE) {
            if (_clickTimeout) {
              clearTimeout(_clickTimeout);
              _clickTimeout = null;
              this.handleDblClick(this.handleRelease);
              return;
            } else {
              _clickTimeout = setTimeout(() => {
                _clickTimeout = null;
              }, DBLCLICK_INTERVAL);
            }
          } else {
            if (dx < CLICK_DISTANCE) {
              this._bouncedLeftOnce = false;
            } else if (dx > CLICK_DISTANCE) {
              this._bouncedRightOnce = false;
            }
            if (dy < CLICK_DISTANCE) {
              this._bouncedYOnce = false;
            }
          }
        }
        this.handleRelease();
      },
      onPanResponderTerminationRequest: (evt, gestureState) => {
        return false;
      },
      onPanResponderTerminate: (evt, gestureState) => {
        this.handleRelease();
      },
      onShouldBlockNativeResponder: (evt, gestureState) => {
        // Returns whether this component should block native components from becoming the JS
        // responder. Returns true by default. Is currently only supported on android.
        return true;
      },
    });
  }

  render() {
    // dont render the image tag until we've already retrieved the size
    if (!this.state.gotSize) {
      return <View style={styles.container} onLayout={this.handleLayout} />;
    }

    const { imageUri, onError } = this.props;

    let animatedStyles = {
      width: this.state.animatedWidth,
      height: this.state.animatedHeight,
      transform: [{ translateX: this.state.animatedX }, { translateY: this.state.animatedY }],
    };

    return (
      <View
        style={styles.container}
        onLayout={this.handleLayout}
        {...this.panResponder.panHandlers}
      >
        <Animated.Image
          style={[styles.image, animatedStyles]}
          source={{ uri: imageUri }}
          resizeMode={'contain'}
          onError={onError}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    //backgroundColor: 'pink',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    //backgroundColor: 'yellow',
    borderRadius: 8,
  },
});
